/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

export const profilePageEn = {
  title: 'Profile',
  pages: {
    yourProfile: 'Your Profile',
    changeEmail: 'Change Email',
    changePassword: 'Change Password',
    security: 'Security',
    dataPrivacy: 'Data Privacy',
    organization: 'Organization'
  },
  alerts: {
    email: {
      resend: 'Resend email',
      cancel: "Don't change after all",
      confirm: {
        title: 'Please confirm your email address',
        text: "We have sent you <strong>instructions to {{email}}</strong>. If you haven't received the message, <strong>please check your spam folder</strong> or request a new email."
      },
      new: {
        title: 'Please confirm your new email address',
        text: "Your new email address <strong>must be confirmed to complete the change.</strong> We have sent you <strong>instructions to {{email}}</strong>. If you haven't received the message, <strong>please check your spam folder</strong> or request a new email."
      }
    },
    accountDeactivated: {
      title: 'Your account is deactivated',
      text: 'Your account has been deactivated. You can no longer sign in. If you have questions, please contact the <0>platform operator</0>.'
    }
  },
  details: {
    title: 'Your Profile',
    profilePicture: 'Profile Picture',
    picture: {
      deletionConfirmationTitle: 'Remove profile picture?',
      deletionConfirmationQuestion: 'Should your profile picture be deleted?',
      acceptDeletionConfirmation: 'Yes, remove!',
      declineDeletionConfirmation: 'No',
      discardChanges: 'Discard',
      acceptChanges: 'Apply',
      tooLargeFile: {
        title: 'Image too large (max. 5 MB)',
        content:
          'Unfortunately, the selected image is too large. Please select an image with a maximum of 5 MB.'
      },
      invalidFileType: {
        title: 'File format not supported',
        content:
          'Unfortunately, we do not support the format of the selected file. Please select an image in "jpg", "jpeg" or "png" format.'
      },
      uploadLogoAlt: 'Round image of a camera',
      defaultAlt: 'Round profile picture',
      uploadButtonLabel: 'Button to select a new profile picture to upload',
      deleteButtonLabel: 'Delete button',
      rotateButtonLabel: 'Rotate button'
    },
    firstName: 'First Name',
    lastName: 'Last Name',
    contact: 'Contact',
    phoneNumber: 'Phone',
    required: 'Please provide your {{field}}.',
    notEmpty: '{{field}} cannot be empty.'
  },
  email: {
    title: 'Change Email',
    email: 'Email',
    password: 'Password',
    totp: 'Security Code',
    verified: 'Verified Email',
    notVerified: 'Not yet verified Email',
    new: 'New Email Address',
    required: 'Please provide an email address.',
    invalid: 'Please provide a valid email address.',
    equal: ' Please provide a different email address.',
    passwordRequired:
      'Please enter your current password to change your email.',
    totpRequired: 'Please enter the displayed code from your authenticator app.'
  },
  password: {
    title: 'Change Password',
    reset: 'Reset Password',
    default:
      'Select "Reset Password" and we will send instructions to your email address.',
    identityProvider:
      'You registered with us using your {{identityProvider}} account. Please visit {{identityProvider}} to change your {{identityProvider}} password.',
    connectingWord: 'or'
  },
  security: {
    title: 'Security',
    heading: 'Two-Factor Authentication',
    description:
      'With two-factor authentication (2FA), your account remains secure even if someone knows your password. A one-time code serves as <strong>additional protection</strong> when logging in. You receive the code via an app on your smartphone.',
    disableText: 'Select "Deactivate" to turn off two-factor authentication.',
    activateText:
      'Select "Activate" and we will explain step by step what to do!',
    active: 'Activated',
    activate: 'Activate',
    deactive: 'Deactivated',
    deactivate: 'Deactivate',
    cancel: 'Cancel',
    confirm: {
      deactivate: {
        title: 'Deactivate 2FA?',
        content:
          'The protection of your account will be reduced and you will no longer be able to access solutions that require two-factor authentication.'
      }
    }
  },
  dataPrivacy: {
    title: 'Data Privacy',
    requestReport: 'Request Data Report',
    requestDescription:
      'Request a data report and receive a copy of all data we have stored about you. You will receive the link to the data report <strong>within 48 hours to your email address</strong>.',
    deleteAccount: 'Delete Account',
    deleteDescription:
      'Unsatisfied or have questions? Maybe we can find a solution together. Please feel free to contact <0>our support</0>.<br></br> <br></br> If you delete your account, your account and the data stored with us will be <strong>irreversibly</strong> removed. Please note that after deletion, <strong>access to all linked solutions will also be lost</strong>.',
    deleteConfirmation: {
      title: 'Permanently delete account?',
      content:
        'The deletion of your account cannot be undone. Do you want to continue?',
      confirm: 'Delete account',
      cancel: 'Cancel'
    },
    deleteBlocked: {
      title: 'Account cannot be deleted',
      content:
        'Before you can delete your account, you must first remove all offers and close or cancel all booking requests and bookings in your organizations. Currently remaining: Offers: {{offerCount}}, Booking requests: {{bookingRequestCount}}, Bookings: {{bookingCount}}.',
      cancel: 'Close',
      confirm: 'Got it'
    },
    deleteBlockedLastPlatformAdmin: {
      title: 'Account cannot be deleted',
      content:
        'You are the only active portal administrator. Assign another administrator before you can delete your account.',
      cancel: 'Close',
      confirm: 'Got it'
    }
  },
  deleted: {
    message:
      'Your account has been deleted.<br></br>Thank you for your time<br></br> at {{appName}}!',
    toLandingPage: 'To {{appName}}'
  },
  common: {
    required: '* Required field'
  },
  steps: {
    step1: {
      title: 'Step 1',
      description: 'Create an account and then confirm your email address.'
    },
    step2: {
      title: 'Step 2',
      description: 'Complete your profile so you can use all features.'
    }
  },
  forms: {
    account: {
      title: 'Account',
      fields: {
        accountType: {
          label: 'Account Type *',
          placeholder: 'Please select',
          options: {
            private: 'Private',
            business: 'Business'
          }
        }
      }
    },
    address: {
      businessSectionTitle: 'Billing address',
      formTitle: 'Address',
      formSubmitButtonLabel: 'Save',
      formFieldLabels: {
        name: 'Name',
        street: 'Street',
        houseNumber: 'House Number',
        postCode: 'Postal Code',
        city: 'City'
      },
      companyName: 'Company/Association',
      vatNumber: 'VAT Number',
      billingHint:
        'The billing address is only used for bookings and is not synchronized with your organization.',
      saving: 'Saving...'
    }
  }
};
